import { LatLng, LatLngBounds } from 'leaflet';

/**
 * Berechnet die konvexe Hülle einer Punktmenge (Andrew's Monotone Chain Algorithmus)
 */
export function computeConvexHull(points: LatLng[]): LatLng[] {
	if (points.length < 3) return points;

	const sorted = [...points].sort((a, b) => {
		if (a.lng !== b.lng) return a.lng - b.lng;
		return a.lat - b.lat;
	});

	const cross = (o: LatLng, a: LatLng, b: LatLng): number => {
		return (a.lng - o.lng) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lng - o.lng);
	};

	const lower: LatLng[] = [];
	for (const p of sorted) {
		while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
			lower.pop();
		}
		lower.push(p);
	}

	const upper: LatLng[] = [];
	for (let i = sorted.length - 1; i >= 0; i--) {
		const p = sorted[i];
		while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
			upper.pop();
		}
		upper.push(p);
	}

	lower.pop();
	upper.pop();

	return [...lower, ...upper];
}

/**
 * Berechnet den Zentroid (Schwerpunkt) eines Polygons
 */
export function computeCentroid(polygon: LatLng[]): LatLng {
	let sumLat = 0, sumLng = 0;
	for (const p of polygon) {
		sumLat += p.lat;
		sumLng += p.lng;
	}
	return new LatLng(sumLat / polygon.length, sumLng / polygon.length);
}

/**
 * Berechnet die Bounding Box für ein Polygon
 */
export function getBoundsForPolygon(polygon: LatLng[]): LatLngBounds {
	if (polygon.length === 0) {
		throw new Error('Polygon darf nicht leer sein');
	}

	let minLat = polygon[0].lat;
	let maxLat = polygon[0].lat;
	let minLng = polygon[0].lng;
	let maxLng = polygon[0].lng;

	for (const p of polygon) {
		if (p.lat < minLat) minLat = p.lat;
		if (p.lat > maxLat) maxLat = p.lat;
		if (p.lng < minLng) minLng = p.lng;
		if (p.lng > maxLng) maxLng = p.lng;
	}

	return new LatLngBounds(
		new LatLng(minLat, minLng),
		new LatLng(maxLat, maxLng)
	);
}

/**
 * Prüft ob ein Punkt innerhalb eines Polygons liegt (Ray-Casting Algorithmus)
 */
export function isPointInPolygon(point: LatLng, polygon: LatLng[]): boolean {
	let inside = false;
	const x = point.lng, y = point.lat;

	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const xi = polygon[i].lng, yi = polygon[i].lat;
		const xj = polygon[j].lng, yj = polygon[j].lat;

		if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
			inside = !inside;
		}
	}

	return inside;
}

/**
 * Prüft ob sich zwei konvexe Hüllen überlappen
 */
export function hullsOverlap(hull1: LatLng[], hull2: LatLng[]): boolean {
	const bounds1 = getBoundsForPolygon(hull1);
	const bounds2 = getBoundsForPolygon(hull2);

	if (!bounds1.overlaps(bounds2)) {
		return false;
	}

	for (const p of hull1) {
		if (isPointInPolygon(p, hull2)) {
			return true;
		}
	}
	for (const p of hull2) {
		if (isPointInPolygon(p, hull1)) {
			return true;
		}
	}

	return edgesIntersect(hull1, hull2);
}

/**
 * Prüft ob sich die Kanten zweier Polygone schneiden
 */
export function edgesIntersect(poly1: LatLng[], poly2: LatLng[]): boolean {
	for (let i = 0; i < poly1.length; i++) {
		const a1 = poly1[i];
		const a2 = poly1[(i + 1) % poly1.length];

		for (let j = 0; j < poly2.length; j++) {
			const b1 = poly2[j];
			const b2 = poly2[(j + 1) % poly2.length];

			if (segmentsIntersect(a1, a2, b1, b2)) {
				return true;
			}
		}
	}
	return false;
}

/**
 * Prüft ob sich zwei Liniensegmente schneiden
 */
export function segmentsIntersect(a1: LatLng, a2: LatLng, b1: LatLng, b2: LatLng): boolean {
	const ccw = (A: LatLng, B: LatLng, C: LatLng): boolean => {
		return (C.lat - A.lat) * (B.lng - A.lng) > (B.lat - A.lat) * (C.lng - A.lng);
	};
	return ccw(a1, b1, b2) !== ccw(a2, b1, b2) && ccw(a1, a2, b1) !== ccw(a1, a2, b2);
}

/**
 * Berechnet die Fläche eines Polygons in Quadratmetern
 */
export function calculatePolygonArea(polygon: LatLng[]): number {
	if (polygon.length < 3) return 0;

	const avgLat = polygon.reduce((sum, p) => sum + p.lat, 0) / polygon.length;
	const latRadians = avgLat * Math.PI / 180;

	const metersPerDegreeLat = 111320;
	const metersPerDegreeLng = 111320 * Math.cos(latRadians);

	let area = 0;
	for (let i = 0; i < polygon.length; i++) {
		const j = (i + 1) % polygon.length;
		const xi = polygon[i].lng * metersPerDegreeLng;
		const yi = polygon[i].lat * metersPerDegreeLat;
		const xj = polygon[j].lng * metersPerDegreeLng;
		const yj = polygon[j].lat * metersPerDegreeLat;
		area += xi * yj - xj * yi;
	}

	return Math.abs(area) / 2;
}

/**
 * Berechnet die Fläche einer Bounding Box in Quadratmetern
 */
export function calculateBoundsArea(bounds: LatLngBounds): number {
	const sw = bounds.getSouthWest();
	const ne = bounds.getNorthEast();

	const avgLat = (sw.lat + ne.lat) / 2;
	const latRadians = avgLat * Math.PI / 180;

	const metersPerDegreeLat = 111320;
	const metersPerDegreeLng = 111320 * Math.cos(latRadians);

	const widthMeters = (ne.lng - sw.lng) * metersPerDegreeLng;
	const heightMeters = (ne.lat - sw.lat) * metersPerDegreeLat;

	return Math.abs(widthMeters * heightMeters);
}

/**
 * Berechnet einen visuellen Mittelpunkt innerhalb eines Polygons.
 * Verwendet den Centroid, falls er innerhalb liegt; sonst Sampling entlang der Mittellinie.
 */
export function computeVisualCenter(polygon: LatLng[]): LatLng {
	if (polygon.length < 3) return computeCentroid(polygon);

	const centroid = computeCentroid(polygon);
	if (isPointInPolygon(centroid, polygon)) return centroid;

	// Fallback: Sample-Punkte entlang der horizontalen Mittellinie
	const bounds = getBoundsForPolygon(polygon);
	const midLat = centroid.lat;
	const steps = 20;
	let bestPoint = centroid;
	let bestDist = Infinity;

	for (let i = 0; i <= steps; i++) {
		const lng = bounds.getWest() + (bounds.getEast() - bounds.getWest()) * i / steps;
		const candidate = new LatLng(midLat, lng);
		if (isPointInPolygon(candidate, polygon)) {
			const dLat = candidate.lat - centroid.lat;
			const dLng = candidate.lng - centroid.lng;
			const dist = dLat * dLat + dLng * dLng;
			if (dist < bestDist) {
				bestDist = dist;
				bestPoint = candidate;
			}
		}
	}

	return bestPoint;
}

/**
 * Führt überlappende Hüllen zusammen durch Bildung einer neuen konvexen Hülle
 */
export function mergeOverlappingHulls(hulls: LatLng[][]): LatLng[][] {
	if (hulls.length <= 1) return hulls;

	const result = [...hulls];
	let merged = true;

	while (merged) {
		merged = false;

		for (let i = 0; i < result.length && !merged; i++) {
			for (let j = i + 1; j < result.length && !merged; j++) {
				if (hullsOverlap(result[i], result[j])) {
					const combined = [...result[i], ...result[j]];
					const newHull = computeConvexHull(combined);

					result[i] = newHull;
					result.splice(j, 1);
					merged = true;
				}
			}
		}
	}

	return result;
}
